import { useState, useEffect, useCallback } from 'react';

const usePaginatedTable = (fetchFunction, initialParams = {}) => {
  const [data, setData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10); // Default page size
  const [ordering, setOrdering] = useState(initialParams.ordering || '');
  const [search, setSearch] = useState(initialParams.search || '');
  const [filters, setFilters] = useState(initialParams.filters || {});

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const params = {
        page,
        size: pageSize,
        ordering: ordering || undefined,
        search: search || undefined,
        ...filters,
      };
      const response = await fetchFunction(params);
      setData(response.results);
      setTotalCount(response.count);
    } catch (err) {
      setError(err);
    } finally {
      setIsLoading(false);
    }
  }, [page, pageSize, ordering, search, filters, fetchFunction]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const setSort = newOrdering => {
    setPage(1);
    setOrdering(newOrdering);
  };

  const setSearchQuery = newSearch => {
    setPage(1);
    setSearch(newSearch);
  };

  const setPageSizeChange = newPageSize => {
    setPage(1);
    setPageSize(newPageSize);
  };

  const goToPage = pageNumber => {
    setPage(pageNumber);
  };

  const setAdditionalFilters = newFilters => {
    setPage(1);
    setFilters(newFilters);
  };

  const totalPages = Math.ceil(totalCount / pageSize);
  const loadNext = () => goToPage(page + 1);
  const loadPrevious = () => goToPage(page - 1);

  return {
    data,
    isLoading,
    error,
    pagination: {
      currentPage: page,
      totalPage: totalPages,
      totalCount: totalCount,
      showSize: pageSize,
    },
    onPageChangeByNumber: goToPage,
    onSizeChange: setPageSizeChange,
    onPreviousPage: loadPrevious,
    onNextPage: loadNext,
    setSort,
    setSearchQuery,
    setAdditionalFilters,
    refetch: fetchData,
  };
};

export default usePaginatedTable;
